/**
 * Normalized API error. The backend sends `{ error: { code, message, details } }`
 * on failure — `unwrap` turns any non-2xx openapi-fetch result into a thrown
 * ApiError so TanStack Query surfaces it as `error`.
 */
export interface ApiErrorBody {
  code?: string;
  message?: string;
  details?: Record<string, unknown>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly details?: Record<string, unknown>;

  constructor(status: number, body?: ApiErrorBody) {
    super(body?.message ?? `Request failed (${status})`);
    this.name = "ApiError";
    this.status = status;
    this.code = body?.code;
    this.details = body?.details;
  }
}

/**
 * Unwrap an openapi-fetch result: return `data` on success, throw `ApiError`
 * otherwise. Every feature `api/` fn pipes its call through this.
 */
export function unwrap<T>(result: {
  data?: T;
  error?: unknown;
  response: Response;
}): T {
  if (result.error !== undefined || !result.response.ok) {
    const body = (result.error as { error?: ApiErrorBody } | undefined)?.error;
    throw new ApiError(result.response.status, body);
  }
  return result.data as T;
}
